import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Package, Plus, Trash2, Loader2, Search, Pencil, X, Check, Hash
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EmptyState from "../components/EmptyState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HSNLookup({ onSelect, currentCode }) {
  const [query, setQuery] = useState(currentCode || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/masters/hsn`, {
          params: { search: query, limit: 8 },
          withCredentials: true,
        });
        setResults(res.data.items || []);
        setOpen(true);
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={e => { setQuery(e.target.value); onSelect(e.target.value); }}
        placeholder="Search HSN/SAC..."
        className="text-sm font-mono"
        data-testid="hsn-lookup-input"
      />
      {searching && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {results.map(h => (
            <button
              key={h.code}
              type="button"
              onClick={() => { onSelect(h.code, h.gst_rate); setQuery(h.code); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-50 last:border-0"
              data-testid={`hsn-option-${h.code}`}
            >
              <span className="font-mono font-medium text-slate-900">{h.code}</span>
              <span className="text-slate-500 ml-2">{h.description}</span>
              <span className="text-blue-600 ml-2 font-medium">{h.gst_rate}%</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductMasterPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", item_type: "product", hsn_code: "", default_gst_rate: 18, unit: "", default_price: "", description: ""
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/masters/products`, {
        params: search ? { search } : {},
        withCredentials: true,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchProducts(); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const resetForm = () => {
    setForm({ name: "", item_type: "product", hsn_code: "", default_gst_rate: 18, unit: "", default_price: "", description: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const { item_type: _it, ...formRest } = form;
      const payload = {
        ...formRest,
        kind: form.item_type || "product",  // backend canonical field
        default_gst_rate: parseFloat(form.default_gst_rate) || 18,
        default_price: form.default_price ? parseFloat(form.default_price) : null,
      };
      if (editingId) {
        await axios.put(`${API}/masters/products/${editingId}`, payload, { withCredentials: true });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/masters/products`, payload, { withCredentials: true });
        toast.success("Product added");
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name || "",
      item_type: product.kind || product.item_type || "product",
      hsn_code: product.hsn_code || "",
      default_gst_rate: product.default_gst_rate || 18,
      unit: product.unit || "",
      default_price: product.default_price || "",
      description: product.description || "",
    });
    setEditingId(product.product_id);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/masters/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      fetchProducts();
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const fmt = (a) => a ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a) : "—";

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="product-master-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Products & Services</h1>
            <p className="text-sm text-slate-500">Manage your product catalog with HSN/SAC codes and GST rates</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>

        {/* Add/Edit Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6"
              data-testid="product-form"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {editingId ? "Edit Product" : "Add New Product"}
                </h2>
                <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Type</label>
                  <div className="grid grid-cols-2 gap-2 max-w-md">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, item_type: "product", unit: prev.item_type === "service" ? "" : prev.unit }))}
                      className={`text-left rounded-lg border p-3 transition-all ${form.item_type === "product" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="item-type-product"
                    >
                      <p className="text-sm font-semibold text-slate-900">Product</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Tangible goods · Has unit & quantity</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, item_type: "service", unit: "" }))}
                      className={`text-left rounded-lg border p-3 transition-all ${form.item_type === "service" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="item-type-service"
                    >
                      <p className="text-sm font-semibold text-slate-900">Service</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Time / consultation · SAC-based</p>
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{form.item_type === "service" ? "Service" : "Product"} Name *</label>
                  <Input
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={form.item_type === "service" ? "e.g. IT Consulting" : "e.g. T-shirt M-size"}
                    data-testid="field-product-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{form.item_type === "service" ? "SAC Code" : "HSN Code"}</label>
                  <HSNLookup
                    currentCode={form.hsn_code}
                    onSelect={(code, rate) => {
                      setForm(prev => ({
                        ...prev,
                        hsn_code: code,
                        ...(rate !== undefined ? { default_gst_rate: rate } : {}),
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GST Rate (%)</label>
                  <Input
                    type="number"
                    value={form.default_gst_rate}
                    onChange={e => setForm(prev => ({ ...prev, default_gst_rate: e.target.value }))}
                    placeholder="18"
                    data-testid="field-gst-rate"
                  />
                </div>
                {form.item_type !== "service" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                    <Input
                      value={form.unit}
                      onChange={e => setForm(prev => ({ ...prev, unit: e.target.value }))}
                      placeholder="Hrs / Nos / Kg"
                      data-testid="field-unit"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Default Price</label>
                  <Input
                    type="number"
                    value={form.default_price}
                    onChange={e => setForm(prev => ({ ...prev, default_price: e.target.value }))}
                    placeholder="0.00"
                    data-testid="field-default-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <Input
                    value={form.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                    data-testid="field-description"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-product-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  {editingId ? "Update" : "Add Product"}
                </Button>
                <Button variant="ghost" onClick={resetForm}>Cancel</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products by name or HSN code..."
              className="pl-10"
              data-testid="search-products"
            />
          </div>
        </div>

        {/* Product List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            icon={Package}
            iconBg="bg-indigo-50 text-indigo-600"
            title="No products yet"
            description="Save your products & services once — auto-fill name, HSN, rate & GST on every invoice."
            bullets={[
              "Built-in HSN code lookup for 12,000+ items",
              "GST rate per product so totals are always correct",
              "Type 2-3 letters in the invoice builder to auto-suggest",
            ]}
            primaryAction={{ label: "Add First Product", icon: Plus, onClick: () => { resetForm(); setShowForm(true); } }}
            testId="products-empty-state"
          />
        ) : (
          <div className="space-y-2">
            {products.map(product => (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 hover:shadow-md transition-all group"
                data-testid={`product-row-${product.product_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-slate-900 text-sm">{product.name}</span>
                      {product.hsn_code && (
                        <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                          <Hash className="w-3 h-3 inline -mt-0.5" />{product.hsn_code}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>GST: {product.default_gst_rate}%</span>
                      {product.unit && <span>Unit: {product.unit}</span>}
                      {product.default_price && <span>Price: {fmt(product.default_price)}</span>}
                      {product.description && <span className="truncate max-w-[200px]">{product.description}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-1.5 text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-${product.product_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.product_id)}
                      className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-${product.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
